import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FALLBACK_LOCALE, isLocale } from "@offertli/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { AuthLayout } from "../components/layout/AuthLayout.js";
import { Button } from "../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { Field } from "../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { ProgressSpinner } from "@primereact/ui/progressspinner";
import { apiGet, apiPost, isApiError } from "../lib/api.js";
import { useInvalidateSession, type SessionUser } from "../lib/auth.js";
import { translateError } from "../lib/form.js";

const PASSWORD_MIN = 10;

const acceptSchema = z
  .object({
    password: z
      .string()
      .min(PASSWORD_MIN, "validation.passwordMin")
      .regex(/[A-Za-z]/, "validation.passwordLetter")
      .regex(/[0-9]/, "validation.passwordNumber"),
    confirmPassword: z.string().min(1, "validation.required"),
    acceptTerms: z
      .boolean()
      .refine((value) => value, "validation.termsRequired"),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ["confirmPassword"],
    message: "validation.passwordMismatch",
  });

type AcceptForm = z.infer<typeof acceptSchema>;

interface InvitationInfo {
  email: string;
  firstName: string;
  lastName: string;
}

export function AcceptInvitationPage() {
  const { t, i18n } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const invalidateSession = useInvalidateSession();

  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () =>
      apiGet<InvitationInfo>(`/invitations/${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AcceptForm>({
    resolver: zodResolver(acceptSchema),
    defaultValues: { password: "", confirmPassword: "", acceptTerms: false },
  });

  const language = i18n.language.toLowerCase().split(/[-_]/)[0];
  const locale = isLocale(language) ? language : FALLBACK_LOCALE;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await apiPost<{ user: SessionUser }>(
        `/invitations/${encodeURIComponent(token)}/accept`,
        {
          password: values.password,
          locale,
          acceptTerms: true,
        },
      );
      await invalidateSession();
      navigate("/", { replace: true });
    } catch (error) {
      if (isApiError(error)) {
        if (error.status === 410) {
          await invitation.refetch();
          return;
        }

        let hasFieldError = false;
        if (error.fields?.password) {
          const message =
            error.fields.password === "Password must contain a letter"
              ? "validation.passwordLetter"
              : error.fields.password === "Password must contain a number"
                ? "validation.passwordNumber"
                : error.fields.password.startsWith("Password must be at least")
                  ? "validation.passwordMin"
                  : "validation.passwordPolicy";
          setError("password", { message });
          hasFieldError = true;
        }
        if (error.fields?.acceptTerms) {
          setError("acceptTerms", { message: "validation.termsRequired" });
          hasFieldError = true;
        }
        if (error.fields?.locale) {
          setError("root", { message: t("validation.localeUnsupported") });
          hasFieldError = true;
        }
        if (hasFieldError) return;

        if (error.status === 429) {
          setError("root", { message: t("errors.tooManyAttempts") });
          return;
        }
      }
      setError("root", { message: t("errors.generic") });
    }
  });

  if (invitation.isLoading)
    return (
      <div className="flex min-h-dvh items-center justify-center bg-app-background">
        <ProgressSpinner.Root aria-label={t("common.loading")}>
          <ProgressSpinner.Track />
          <ProgressSpinner.Range />
        </ProgressSpinner.Root>
      </div>
    );

  if (invitation.isError || !invitation.data) {
    return (
      <AuthLayout
        title={t("invite.invalidTitle")}
        subtitle={t("invite.invalidMessage")}
      >
        <Button
          as={Link}
          to="/login"
          variant="outlined"
          severity="secondary"
          className="w-full"
        >
          {t("invite.toLogin")}
        </Button>
      </AuthLayout>
    );
  }

  const info = invitation.data;

  return (
    <AuthLayout title={t("invite.title")} subtitle={t("invite.subtitle")}>
      <div className="mb-4 rounded-lg bg-app-background px-4 py-3 text-sm text-app-text">
        {t("invite.invitedAs")}{" "}
        <span className="font-medium text-app-text">
          {info.firstName} {info.lastName} · {info.email}
        </span>
      </div>
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field
          label={t("invite.password")}
          htmlFor="password"
          error={
            errors.password?.message
              ? t(errors.password.message, { min: PASSWORD_MIN })
              : undefined
          }
          required
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.password}
            {...register("password")}
          />
        </Field>
        <Field
          label={t("invite.confirmPassword")}
          htmlFor="confirmPassword"
          error={translateError(errors.confirmPassword?.message, t)}
          required
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.confirmPassword}
            {...register("confirmPassword")}
          />
        </Field>
        <div className="space-y-1.5">
          <label
            htmlFor="accept-terms"
            className="flex items-center gap-2 text-sm text-app-text"
          >
            <Checkbox.Root
              inputId="accept-terms"
              checked={watch("acceptTerms")}
              invalid={Boolean(errors.acceptTerms)}
              aria-describedby={
                errors.acceptTerms ? "accept-terms-error" : undefined
              }
              onCheckedChange={(event: { checked: boolean }) =>
                setValue("acceptTerms", event.checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            >
              <Checkbox.Box>
                <Checkbox.Indicator match="checked">
                  <i className="pi pi-check" />
                </Checkbox.Indicator>
              </Checkbox.Box>
            </Checkbox.Root>
            <span>{t("invite.acceptTerms")}</span>
          </label>
          {errors.acceptTerms ? (
            <p
              id="accept-terms-error"
              role="alert"
              className="text-sm text-danger"
            >
              {translateError(errors.acceptTerms.message, t)}
            </p>
          ) : null}
        </div>
        {errors.root ? (
          <p
            role="alert"
            className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {errors.root.message}
          </p>
        ) : null}
        <Button
          type="submit"
          size="large"
          loading={isSubmitting || undefined}
          className="w-full"
        >
          {t("invite.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
